import { useCallback, useState, useEffect, } from "../../__compat__/react/main.entry.mjs.mjs";
/**
 * This hook handles setting the position of an HTML element to
 * match the position of a selected tab. Combined with CSS transitions,
 * this accomplishes an animated background that slides to sit behind
 * the selected tab.
 *
 * @param {RefObject<HTMLElement>} parentRef  A ref pointing to a parent
 * of the indicator, to store the dimensions and position of the indicator
 * @param {RefObject<HTMLElement>[]} childRef A ref pointing to the child element of which to calculate the indicator offset and size
 * @param {any[]} dependencies  additional dependencies that should cause the indicator offset and size to be recalculated
 * @returns {boolean} indicatorHasPosition True if the indicator has been given an
 * initial position corresponding to the selected tab.
 */
export const useIndicator = (parentRef, childRef, dependencies = []) => {
    const [indicatorHasPosition, setIndicatorHasPosition] = useState(false);
    const updateIndicator = useCallback(() => {
        var _a, _b, _c, _d;
        if ((parentRef === null || parentRef === void 0 ? void 0 : parentRef.current) && (childRef === null || childRef === void 0 ? void 0 : childRef.current)) {
            parentRef.current.style.setProperty('--indicator-size', ((_b = (_a = childRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) !== null && _b !== void 0 ? _b : 0) + 'px');
            parentRef.current.style.setProperty('--indicator-offset', ((_d = (_c = childRef.current) === null || _c === void 0 ? void 0 : _c.offsetLeft) !== null && _d !== void 0 ? _d : 0) + 'px');
        }
        setIndicatorHasPosition(Boolean((parentRef === null || parentRef === void 0 ? void 0 : parentRef.current) && (childRef === null || childRef === void 0 ? void 0 : childRef.current)));
    }, [parentRef, childRef]);
    // Update the indicator when the selected tab changes
    // Also update for changes to the tabRefs or parentRef, since
    // the updateIndicator function will change
    useEffect(updateIndicator, [
        parentRef,
        childRef,
        updateIndicator,
        ...dependencies,
    ]);
    // Update the indicator for any resizes and when fonts are done loading
    useEffect(() => {
        var _a;
        (_a = document.fonts) === null || _a === void 0 ? void 0 : _a.ready.then(updateIndicator);
        window.addEventListener('resize', updateIndicator);
        const observer = new MutationObserver(updateIndicator);
        if (parentRef.current) {
            observer.observe(parentRef.current, {
                subtree: true,
                childList: true,
                characterData: true,
            });
        }
        return () => {
            window.removeEventListener('resize', updateIndicator);
            observer.disconnect();
        };
    }, [updateIndicator, parentRef]);
    return indicatorHasPosition;
};
//# sourceMappingURL=useIndicator.mjs.mjs.map